'use no memo';

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { HStack, VStack } from '@/components/Layout';
import { Loader } from '@/components/Loading';
import { Breadcrumb } from '@/components/Navigation/Breadcrumb';
import NavigationCard, { type NavigationSectionGroup } from '@/components/Navigation/NavigationCard';
import { Text } from '@/components/Typography/Text';
import { toast } from '@/components/ui/use-toast';
import { CFormSectionDetailLayout } from '@/features/c-form/CFormSectionDetailLayout';
import type { ApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { AttributeRenderer } from './profile.$profileId/-components/AttributeRenderer';
import { CertificateSection } from './profile.$profileId/-components/CertificateSection';
import { EGovAccountSection } from './profile.$profileId/-components/EGovAccountSection';
import { SECTION_KEYS } from './profile.$profileId/-constants';
import { useGetGovProfileDetail, useSaveApplicant, useSaveContact, useGetCertificateStatus, useUploadCertificate, useGetOAuthStatus, useInitiateOAuth, useLogoutOAuth } from './profile.$profileId/-query/hooks';
import type { GovProfileSection } from './profile.$profileId/-query/type';
import { GovProfileDetailFormStore, useGovProfileDetailFormStore } from './profile.$profileId/-store/store';
export const Route = createFileRoute('/_staff/labor-core/settings/profile/$profileId')({
  component: ProfileDetailPage
});
function ProfileDetailPage() {
  const store = GovProfileDetailFormStore.createStore();
  return <GovProfileDetailFormStore.Provider value={store}>
      <ProfileDetailContent />
    </GovProfileDetailFormStore.Provider>;
}
function ProfileDetailContent() {
  const {
    profileId
  } = Route.useParams();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.laborCore.settings'
  });
  const {
    data,
    isLoading,
    error
  } = useGetGovProfileDetail(profileId);
  const {
    actions
  } = useGovProfileDetailFormStore();
  const saveApplicantMutation = useSaveApplicant(profileId, {
    onError: (error: ApiError) => {
      const message = actions.onApiError(error);
      if (message) {
        toast({
          variant: 'destructive',
          description: message
        });
      }
    }
  });
  const saveContactMutation = useSaveContact(profileId, {
    onError: (error: ApiError) => {
      const message = actions.onApiError(error);
      if (message) {
        toast({
          variant: 'destructive',
          description: message
        });
      }
    }
  });
  const {
    data: certificateData,
    isLoading: isCertificateLoading
  } = useGetCertificateStatus(profileId);
  const uploadCertificateMutation = useUploadCertificate(profileId);
  const {
    data: oauthData,
    isLoading: isOAuthLoading
  } = useGetOAuthStatus(profileId);
  const initiateOAuthMutation = useInitiateOAuth(profileId);
  const logoutOAuthMutation = useLogoutOAuth(profileId);
  useEffect(() => {
    if (data) {
      actions.reset(data);
    }
  }, [data, actions]);
  const applicantSections = useMemo(() => data?.sections.filter(s => String(s.sectionKey) === SECTION_KEYS.APPLICATION_APPLICANT_INFORMATION), [data?.sections]);
  const contactSections = useMemo(() => data?.sections.filter(s => String(s.sectionKey) === SECTION_KEYS.APPLICATION_CONTACT_INFORMATION), [data?.sections]);
  const handleSaveApplicant = useCallback(async () => {
    const {
      isValid,
      errorMessages
    } = actions.validate(SECTION_KEYS.APPLICATION_APPLICANT_INFORMATION, field => t('validation.required', {
      field
    }));
    if (!isValid) {
      if (errorMessages.length > 0) {
        toast({
          variant: 'destructive',
          description: errorMessages.join('\n')
        });
      }
      throw new Error('Validation failed');
    }
    const updateData = actions.getUpdateRequest();
    const sectionsToSave = updateData.sections.filter(s => String(s.sectionKey) === SECTION_KEYS.APPLICATION_APPLICANT_INFORMATION);
    await saveApplicantMutation.mutateAsync({
      sections: sectionsToSave
    });
  }, [actions, saveApplicantMutation, t]);
  const handleResetApplicant = useCallback(() => {
    if (data) {
      actions.resetSection(SECTION_KEYS.APPLICATION_APPLICANT_INFORMATION, data);
    }
  }, [actions, data]);
  const handleSaveContact = useCallback(async () => {
    const {
      isValid,
      errorMessages
    } = actions.validate(SECTION_KEYS.APPLICATION_CONTACT_INFORMATION, field => t('validation.required', {
      field
    }));
    if (!isValid) {
      if (errorMessages.length > 0) {
        toast({
          variant: 'destructive',
          description: errorMessages.join('\n')
        });
      }
      throw new Error('Validation failed');
    }
    const updateData = actions.getUpdateRequest();
    const sectionsToSave = updateData.sections.filter(s => String(s.sectionKey) === SECTION_KEYS.APPLICATION_CONTACT_INFORMATION);
    await saveContactMutation.mutateAsync({
      sections: sectionsToSave
    });
  }, [actions, saveContactMutation, t]);
  const handleResetContact = useCallback(() => {
    if (data) {
      actions.resetSection(SECTION_KEYS.APPLICATION_CONTACT_INFORMATION, data);
    }
  }, [actions, data]);
  const renderSectionContent = useCallback((section: GovProfileSection) => <AttributeRenderer attributes={section.attributes} sectionKey={String(section.sectionKey)} />, []);
  if (isLoading) {
    return <div className="flex justify-center items-center py-12">
        <Loader />
      </div>;
  }
  if (error) {
    return <div className="p-6">
        <VStack className="gap-4 items-center">
          <Text variant="h3" className="text-error-on-neutral-fill">
            {t('profileDetail.error.loadFailed')}
          </Text>
          <Text variant="p" className="text-neutral-muted-on-fill">
            {error.message || t('profileDetail.error.unknownError')}
          </Text>
        </VStack>
      </div>;
  }
  if (!data) {
    return <div className="p-6">
        <VStack className="gap-4 items-center">
          <Text variant="p" className="text-neutral-muted-on-fill">
            {t('profileDetail.error.noData')}
          </Text>
        </VStack>
      </div>;
  }
  const breadcrumbs = [{
    label: t('profileDetail.breadcrumbs.laborCore'),
    link: {
      to: '/labor-core' as const
    }
  }, {
    label: t('profileDetail.breadcrumbs.settings'),
    link: {
      to: '/labor-core/settings' as const,
      hash: 'applicant-contacts'
    }
  }];
  const navigationSections: NavigationSectionGroup[] = [{
    titleLabel: '',
    to: '/labor-core/settings/profile/$profileId' as const,
    sections: [{
      label: applicantSections?.[0]?.name ?? t('profileDetail.navigation.applicantInformation'),
      hash: 'applicant'
    }, {
      label: contactSections?.[0]?.name ?? t('profileDetail.navigation.contactInformation'),
      hash: 'contact'
    }, {
      label: t('profileDetail.navigation.certificate'),
      hash: 'certificate'
    }, {
      label: t('profileDetail.navigation.eGovAccount'),
      hash: 'egov-account'
    }]
  }];
  return <VStack className="gap-0">
      <VStack className="bg-neutral-fill drop-shadow-sm px-8 pt-6 pb-4 gap-2">
        <Breadcrumb breadcrumbs={breadcrumbs} />
        <Text className="pl-2 pt-2" variant="h3">
          {data.profileName}
        </Text>
      </VStack>

      <HStack className="p-8 gap-8">
        <nav className="sticky top-8 h-fit w-60">
          <NavigationCard sections={navigationSections} />
        </nav>

        <VStack className="w-full gap-4">
          <div id="applicant">
            <CFormSectionDetailLayout sections={applicantSections} isLoading={false} error={null} linkTo="." showNavigation={false} onSave={handleSaveApplicant} onReset={handleResetApplicant} renderSectionContent={renderSectionContent} />
          </div>

          <div id="contact">
            <CFormSectionDetailLayout sections={contactSections} isLoading={false} error={null} linkTo="." showNavigation={false} onSave={handleSaveContact} onReset={handleResetContact} renderSectionContent={renderSectionContent} />
          </div>

          <div id="certificate">
            <CertificateSection data={certificateData} onUpload={(file, pin) => {
            uploadCertificateMutation.mutate({
              file,
              pin
            });
          }} isLoading={isCertificateLoading || uploadCertificateMutation.isPending} />
          </div>

          <div id="egov-account">
            <EGovAccountSection data={oauthData} onConnect={() => {
            initiateOAuthMutation.mutate();
          }} onLogout={() => {
            logoutOAuthMutation.mutate();
          }} isLoading={isOAuthLoading || initiateOAuthMutation.isPending} isLoggingOut={logoutOAuthMutation.isPending} />
          </div>
        </VStack>
      </HStack>
    </VStack>;
}